/**
 * 차트 색. 대시보드 차트(wijmo palette)가 같이 쓴다.
 * - 밝은 테마와 다크 계열 테마(다크, 화주사 다크) 색을 한 벌씩 둔다. 다크는 어두운 바탕에서 보이게 밝은 색이다.
 * - 다크 여부는 NpThemeService 가 html 에 붙이는 data-bs-theme="dark" 로 본다.
 * - 화면 바탕·글자·선 색은 여기가 아니라 CSS 변수(--np-dash-*, nova-common-styles.scss)를 쓴다.
 */
export interface NovaChartColors {
  /** 매출·정상·완료 */
  success: string;
  /** 지출·위험·미정산 */
  danger: string;
  /** 경고·주의 */
  warning: string;
  /** 대표 계열 (순이익, 차량 수 등) */
  primary: string;
  /** 해당 없음·기타 */
  neutral: string;
  /** 종류별 구성(도넛 등)에 차례로 쓰는 색 */
  series: string[];
}

const LIGHT: NovaChartColors = {
  success: '#3a9b6a',
  danger: '#d9534f',
  warning: '#c9a227',
  primary: '#2f4b7c',
  neutral: '#c3ccd7',
  series: ['#2f4b7c', '#3a9b6a', '#6fa3d6', '#94a3b8', '#c9a227', '#a9cae9'],
};

const DARK: NovaChartColors = {
  success: '#4caf7d',
  danger: '#e5736f',
  warning: '#d9b54a',
  primary: '#8ab4f8',
  neutral: '#6b6b6b',
  series: ['#7aa7e6', '#4caf7d', '#b39ddb', '#a3a3a3', '#d9b54a', '#80cbc4'],
};

/** 다크 계열 테마인지 */
export function isNovaDarkTheme(): boolean {
  return document.documentElement.getAttribute('data-bs-theme') === 'dark';
}

/** 지금 테마의 차트 색 */
export function novaChartColors(): NovaChartColors {
  return isNovaDarkTheme() ? DARK : LIGHT;
}

const paletteCache = new Map<string, string[]>();

/**
 * 지금 테마의 차트 palette. 템플릿 getter 에서 불러도 되게 key·테마별로 같은 배열을 돌려준다
 * (매번 새 배열을 주면 wijmo 가 차트를 계속 다시 그린다).
 * 예: get trendPalette() { return novaChartPalette('settle-trend', c => [c.success, c.danger, c.primary]); }
 */
export function novaChartPalette(key: string, build: (colors: NovaChartColors) => string[]): string[] {
  const dark = isNovaDarkTheme();
  const cacheKey = `${key}:${dark ? 'dark' : 'light'}`;
  let palette = paletteCache.get(cacheKey);
  if (!palette) {
    palette = build(dark ? DARK : LIGHT);
    paletteCache.set(cacheKey, palette);
  }
  return palette;
}
